import React from 'react';
import { DownloadIcon, RotateCcwIcon } from 'lucide-react';
import { useAssessment, useInstrumentConfig } from '../contexts/AssessmentContext';
import { EVIDENCE_KEY, formatDuration } from '../utils/scoring';
import { downloadRecordPdf } from '../utils/pdf';
const BAR_COLORS = ['bg-slate-700', 'bg-slate-400', 'bg-teal-500', 'bg-slate-500', 'bg-teal-700'];
export function Admin() {
  const {
    instrument,
    participantId,
    sessionId,
    elapsed,
    promptCount,
    dwell,
    openedDocs,
    sharedDocs,
    results,
    flags,
    events,
    messages,
    reflection,
    packConfidence,
    submitted,
    startedAt,
    resetSession
  } = useAssessment();
  const config = useInstrumentConfig();
  const confidenceLabel = packConfidence ? config.reflection.confidence.find(c => c.id === packConfidence)?.label || packConfidence : '—';
  const totalDwell = config.tabs.reduce((sum, t) => sum + (dwell[t.id] || 0), 0);
  const docDwell = dwell.documents || 0;
  const sourceShare = totalDwell === 0 ? 0 : Math.round(docDwell / totalDwell * 100);
  const metrics = [{
    label: 'Prompts to assistant',
    value: promptCount.toString(),
    sub: `${messages.length} messages total`
  }, {
    label: 'Documents opened',
    value: `${openedDocs.length} / ${config.documents.length}`,
    sub: openedDocs.join(', ') || 'none'
  }, config.assistant.shareable ? {
    label: 'Shared with assistant',
    value: `${sharedDocs.length} / ${config.documents.length}`,
    sub: sharedDocs.join(', ') || 'nothing shared'
  } : {
    label: 'Time in source documents',
    value: formatDuration(docDwell),
    sub: `${sourceShare}% of workspace time`
  }, {
    label: 'Elapsed',
    value: formatDuration(elapsed),
    sub: submitted ? 'Submitted' : startedAt ? 'In progress' : 'Not started'
  }];
  const dwellBars = config.tabs.map((t, i) => ({
    label: t.label,
    seconds: dwell[t.id] || 0,
    color: t.id === 'documents' ? 'bg-teal-500' : BAR_COLORS[i % BAR_COLORS.length]
  }));
  return <main className="w-full flex-1 bg-slate-100 pb-16" data-id="element-407" data-mp="29b3vutgpiw25">
      <div className="border-b border-slate-800 bg-slate-900" data-id="element-408" data-mp="298r6hv8mdjf2">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-end justify-between gap-4 px-6 py-8" data-id="element-409" data-mp="1ueuwzt0qakq9">
          <div data-id="element-410" data-mp="1zd67m5yukz9j">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-teal-400" data-id="element-411" data-mp="1zeqquzfpovs3">
              Reviewer view · not shown to the participant
            </p>
            <p className="mt-1 font-mono-num text-[11px] text-slate-500" data-id="element-412" data-mp="2s0a8byqwqrfi">
              {config.id} {config.version} · {config.name} · {config.matter}
            </p>
            <h1 className="mt-2 text-2xl font-semibold tracking-tight text-white" data-id="element-413" data-mp="2ib8sc8kd4a1j">
              Session record — {participantId || 'no participant'}{' '}
              <span className="font-mono-num text-base font-normal text-slate-400" data-id="element-414" data-mp="35zs10p7bqou5">{sessionId}</span>
            </h1>
            <p className="mt-1 text-sm text-slate-400" data-id="element-415" data-mp="3atnupfgk1pji">
              A record of what happened. The categories below are descriptions, not scores.
            </p>
          </div>
          <div className="flex items-center gap-2" data-id="element-416" data-mp="udhfrnrxl77t">
            <button type="button" onClick={resetSession} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-4 py-3 text-sm font-medium text-slate-300 transition-colors hover:border-slate-600 hover:text-white" data-id="element-417" data-mp="1935gamsag1uh">
              <RotateCcwIcon className="h-4 w-4" data-id="element-418" data-mp="bfevfmabvygc" />
              Reset session
            </button>
            <button type="button" onClick={() => downloadRecordPdf({
            instrument,
            participantId,
            sessionId,
            elapsedSeconds: elapsed,
            promptCount,
            dwell,
            openedDocs,
            sharedDocs,
            results,
            flags,
            reflection,
            packConfidence,
            messages
          })} className="inline-flex items-center gap-2 rounded-lg bg-teal-400 px-5 py-3 text-sm font-semibold text-slate-900 transition-colors hover:bg-teal-300" data-id="element-419" data-mp="pf65xd1mywpl">
              <DownloadIcon className="h-4 w-4" data-id="element-420" data-mp="3637cipyi6vx5" />
              Download 2-page record
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-6" data-id="element-421" data-mp="3azfzgpixztev">
        <div className="-mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-id="element-422" data-mp="3f1sv2l3z6axx">
          {metrics.map(m => <div key={m.label} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-423" data-mp="3juvn2ot3tcc0">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500" data-id="element-424" data-mp="3orcfvwb90evf">
                {m.label}
              </p>
              <p className="mt-2 font-mono-num text-2xl font-semibold text-slate-900" data-id="element-425" data-mp="2lgtcv29arnn">{m.value}</p>
              <p className="mt-1 truncate text-xs text-slate-500" title={m.sub} data-id="element-426" data-mp="1n3hwnswf7qxd">
                {m.sub}
              </p>
            </div>)}
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]" data-id="element-427" data-mp="1i2txul1ya5sl">
          <section className="rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-428" data-mp="1d63gzvibn664">
            <header className="flex items-center justify-between border-b border-slate-200 px-5 py-4" data-id="element-429" data-mp="187ac2rh3onhk">
              <h2 className="text-sm font-semibold text-slate-900" data-id="element-430" data-mp="25sy12rb883j2">Scenario record</h2>
              <span className="text-xs text-slate-500" data-id="element-431" data-mp="20umow9xglgxo">
                Recommendation:{' '}
                <span className="font-medium text-slate-800" data-id="element-432" data-mp="1w3efhdw4pvki">{confidenceLabel}</span>
              </span>
            </header>
            <ul className="divide-y divide-slate-100" data-id="element-433" data-mp="1r4bhgdekgpm1">
              {results.map(r => {
              const spec = config.scenarios.find(s => s.id === r.scenarioId);
              if (!spec) return null;
              const key = EVIDENCE_KEY[r.category];
              return <li key={r.scenarioId} className="px-5 py-4" data-id="element-434" data-mp="hxje95usofcy">
                    <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-435" data-mp="mv1msv4wgskf">
                      <div className="max-w-xl" data-id="element-436" data-mp="86f2ef0f1amc">
                        <p className="font-mono-num text-[11px] uppercase tracking-wider text-slate-400" data-id="element-437" data-mp="85lxljl0ov6u">
                          {r.scenarioId} · {spec.klass} · {spec.rowId}
                        </p>
                        <p className="mt-1 text-sm text-slate-900" data-id="element-438" data-mp="3v9jsiq7dbw0v">{spec.vehicle}</p>
                      </div>
                      <span className={`shrink-0 rounded-full border px-3 py-1 text-[11.5px] font-semibold ${key.className}`} data-id="element-439" data-mp="38n5f6kwyyba">
                        {key.label}
                      </span>
                    </div>
                    <p className="mt-2 text-xs leading-relaxed text-slate-600" data-id="element-440" data-mp="1jmlrgjn0ji2z">{r.evidence}</p>
                    <p className="mt-1.5 text-[11px] text-slate-400" data-id="element-441" data-mp="1okwyynpu5gi1">
                      Full credit: {spec.fullCredit} · decisive source {spec.decisiveSourceId}
                    </p>
                  </li>;
            })}
            </ul>
          </section>

          <div className="space-y-6" data-id="element-442" data-mp="3is920hkzy82z">
            <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-443" data-mp="3itak360fjuu7">
              <h2 className="text-sm font-semibold text-slate-900" data-id="element-444" data-mp="3e10p3bkgk4l2">Where time went</h2>
              <div className="mt-4 space-y-4" data-id="element-445" data-mp="3rs5smoqy5h0o">
                {dwellBars.map(b => {
                const pct = totalDwell === 0 ? 0 : b.seconds / totalDwell * 100;
                return <div key={b.label} data-id="element-446" data-mp="5kxtw5aj88dm">
                      <div className="flex items-baseline justify-between" data-id="element-447" data-mp="5jwe5nikmxpk">
                        <p className="text-xs font-medium text-slate-700" data-id="element-448" data-mp="nffw0hnr2yr">{b.label}</p>
                        <p className="font-mono-num text-xs text-slate-500" data-id="element-449" data-mp="27e55p4qxs7ur">
                          {formatDuration(b.seconds)} · {Math.round(pct)}%
                        </p>
                      </div>
                      <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-slate-100" data-id="element-450" data-mp="1gkk7k1k716ct">
                        <div className={`h-full rounded-full transition-all duration-500 ${b.color}`} style={{
                      width: `${pct}%`
                    }} data-id="element-451" data-mp="1liloafu5ctl4" />
                      </div>
                    </div>;
              })}
              </div>
            </section>

            <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-452" data-mp="12larr2zwge9d">
              <h2 className="text-sm font-semibold text-slate-900" data-id="element-453" data-mp="12n5b4cvivybc">Entries</h2>
              {Object.keys(flags).length === 0 ? <p className="mt-3 text-sm text-slate-400" data-id="element-454" data-mp="1bmif4q0vnuoo">No flags or corrections recorded.</p> : <ul className="mt-3 space-y-3" data-id="element-455" data-mp="2iyitqwhodu36">
                  {Object.values(flags).map(f => <li key={f.rowId} className="rounded-lg border border-slate-200 p-3" data-id="element-456" data-mp="2mzukx6g76ndh">
                      <p className="font-mono-num text-[11px] text-slate-500" data-id="element-457" data-mp="bcr0pq837kjk">
                        {f.rowId} · {f.kind} · {f.actionType} · sign-off {f.signOff}
                      </p>
                      <p className="mt-1 text-[13px] text-slate-800" data-id="element-458" data-mp="2kp2lylhqgcg">{f.what}</p>
                      <p className="mt-1 text-[11px] text-slate-500" data-id="element-459" data-mp="3ts1kkfu76xtf">
                        Sources: {f.sources.join(', ') || 'none cited'}
                      </p>
                    </li>)}
                </ul>}
            </section>
          </div>
        </div>

        <section className="mt-6 rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-460" data-mp="3frqtbboz7h7q">
          <header className="border-b border-slate-200 px-5 py-4" data-id="element-461" data-mp="3asxhrot920a7">
            <h2 className="text-sm font-semibold text-slate-900" data-id="element-462" data-mp="3aus0qxkynibn">Event log</h2>
            <p className="mt-0.5 text-xs text-slate-500" data-id="element-463" data-mp="1dzch8t9456z8">{events.length} events captured</p>
          </header>
          <div className="max-h-96 overflow-y-auto" data-id="element-464" data-mp="1923uefd0sern">
            {events.length === 0 ? <p className="px-5 py-8 text-center text-sm text-slate-400" data-id="element-465" data-mp="2gp42jan7ec3h">No activity recorded yet.</p> : <table className="w-full text-sm" data-id="element-466" data-mp="2btga8mdfd2tr">
                <tbody data-id="element-467" data-mp="2prd7zm3m7fa0">
                  {[...events].reverse().map(e => <tr key={e.id} className="border-b border-slate-100 last:border-0" data-id="element-468" data-mp="2ksa3q3vgld33">
                      <td className="w-24 whitespace-nowrap px-5 py-2 font-mono-num text-xs text-slate-400" data-id="element-469" data-mp="1x2fs1pmvlukf">
                        {new Date(e.at).toLocaleTimeString()}
                      </td>
                      <td className="w-28 px-2 py-2" data-id="element-470" data-mp="26s90ihrq1tkg">
                        <span className="rounded bg-slate-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600" data-id="element-471" data-mp="26t0vprheppa7">
                          {e.type}
                        </span>
                      </td>
                      <td className="px-5 py-2 text-slate-700" data-id="element-472" data-mp="1e9lkvhvhv6qr">{e.detail}</td>
                    </tr>)}
                </tbody>
              </table>}
          </div>
        </section>
      </div>
    </main>;
}